import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";

type OrgResponse = { domain?: string | null; companyName?: string | null };

type UserRow = {
  id: string;
  email?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string;
  isActive?: boolean;
  jobTitle?: string | null;
};

type UsersResponse = {
  users?: UserRow[];
  pagination?: { total?: number };
};

function displayName(u: UserRow): string {
  return (
    u.name?.trim() ||
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    u.email?.trim() ||
    u.id
  );
}

export function SettingsUsersPage() {
  const [domain, setDomain] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const orgRes = await apiGet<OrgResponse>("/api/me/organization");
      if (cancelled) return;
      const d = orgRes.data?.domain?.trim() || null;
      setDomain(d);
      setCompanyName(orgRes.data?.companyName ?? null);
      if (!orgRes.ok || !d) {
        setError(orgRes.error || "Organization domain required to list users.");
        setUsers([]);
        setLoading(false);
        return;
      }
      const usersRes = await apiGet<UsersResponse>(
        `/api/users?accountDomain=${encodeURIComponent(d)}&limit=100`,
      );
      if (cancelled) return;
      if (!usersRes.ok) {
        setError(usersRes.error || "Could not load users.");
        setUsers([]);
      } else {
        setError(null);
        setUsers(usersRes.data?.users ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [displayName(u), u.email, u.role, u.jobTitle].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [users, query]);

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">People</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Org users via <code className="text-sm">/api/users</code>
            {companyName ? ` · ${companyName}` : null}.
          </p>
        </div>
        <input
          type="search"
          placeholder="Search people"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-xs rounded-md border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-slate-950"
        />
      </div>

      <p className="text-sm text-slate-600 dark:text-slate-400">
        Full invite and access editing lives in{" "}
        <a className="font-medium text-indigo-600 underline dark:text-indigo-300" href="https://directory.atw.net/people">
          ATW Directory
        </a>
        . This list is read-only on One.
      </p>

      {loading ? <p className="text-sm text-slate-500">Loading people…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {!loading && !error ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No people found{domain ? ` for ${domain}` : ""}.
                  </td>
                </tr>
              ) : (
                filtered.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 dark:border-white/5">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{displayName(user)}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{user.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{user.role || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {user.isActive === false ? "Suspended" : "Active"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <Link to="/settings" className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-300">
        ← Settings overview
      </Link>
    </div>
  );
}
